/**
 * Una devolución de una factura que NO está en esta aplicación.
 *
 * Vive suelto y sin importar nada, como `importes.js`, `factura-nueva.js` y
 * `coincidencia.js`: el diálogo importa `@/App` para el axios configurado, y
 * probar aritmética no puede arrastrar la aplicación entera y el router.
 *
 * POR QUÉ EXISTE
 * --------------
 * Angel, 11-sep-2026: la factura original se emitió con la aplicación
 * anterior, así que aquí no hay nada de donde copiar importes. Se escriben.
 *
 * LA REGLA DEL SIGNO
 * ------------------
 * Aquí todo va en POSITIVO: se escribe lo que se cobró. El menos lo pone el
 * servidor, en un solo sitio. Pedirle el signo a quien teclea es pedirle que
 * se equivoque, y un menos olvidado no es una devolución: es una factura más,
 * con número de abono.
 */

// El cálculo del impuesto vive en `impuestos.js` desde el 19-sep-2026: lo
// necesitan esta pantalla y la de factura normal. Se re-exporta para no
// romper a quien lo importaba de aquí.
import { regimenDelCliente, importeDeLineaEscrita } from './impuestos';

export { regimenDelCliente, importeDeLineaEscrita };

/**
 * Lo que se va a devolver, en POSITIVO: { base, cuota, total }.
 *
 * La pantalla lo pinta con el menos delante. El documento que sale lleva estos
 * mismos importes en negativo.
 */
export function totalDeLaDevolucion(lineas, cliente) {
  const { tipo, regimen } = regimenDelCliente(cliente);
  const suma = (lineas || []).reduce((acc, linea) => {
    const { base, cuota } = importeDeLineaEscrita(linea, tipo, regimen);
    return { base: acc.base + base, cuota: acc.cuota + cuota };
  }, { base: 0, cuota: 0 });

  const base = Math.round(suma.base * 100) / 100;
  const cuota = Math.round(suma.cuota * 100) / 100;
  return { base, cuota, total: Math.round((base + cuota) * 100) / 100 };
}

/**
 * Por qué todavía no se puede emitir. Devuelve null cuando sí se puede.
 *
 * No repite las reglas del servidor: se adelanta a las que cuestan una vuelta
 * entera. Quien decide sigue siendo el servidor, que se niega con el motivo
 * exacto.
 */
export function motivoParaNoEmitir(formulario) {
  const f = formulario || {};
  if (!f.customer_id) return 'Elige el cliente al que se le devuelve.';
  if (!String(f.factura_original_numero || '').trim()) {
    return 'Escribe el número de la factura que se rectifica.';
  }
  if (!f.factura_original_fecha) return 'Escribe la fecha de la factura que se rectifica.';
  if (String(f.motivo || '').trim().length < 3) return 'Falta el motivo de la devolución.';
  if (!f.payment_method) return 'Elige cómo se devuelve el dinero.';
  if (!(f.line_items || []).length) return 'Añade al menos un concepto a devolver.';
  return null;
}

/** El cuerpo del POST /invoices/returns a partir del formulario. */
export function cuerpoDeDevolucion(formulario) {
  const f = formulario || {};
  return {
    customer_id: f.customer_id,
    // En positivo, y sin importes: los calcula el servidor con el impuesto que
    // le toque a ese cliente. Mandar el total desde aquí permitiría emitir un
    // abono cuyo total no cuadra con sus propias líneas.
    line_items: (f.line_items || []).map((l) => ({
      description: l.description,
      quantity: Math.abs(Number(l.quantity) || 0),
      unit_price: Math.abs(Number(l.unit_price) || 0),
      discount: Math.abs(Number(l.discount) || 0),
      // null y no 0: null significa "el que le toque al cliente", 0 significa
      // "esta línea no lleva impuesto". No son lo mismo.
      tax_rate: l.tax_rate === '' || l.tax_rate === null || l.tax_rate === undefined
        ? null : Number(l.tax_rate),
    })),
    motivo: String(f.motivo || '').trim(),
    factura_original_numero: String(f.factura_original_numero || '').trim(),
    factura_original_fecha: f.factura_original_fecha || null,
    payment_method: f.payment_method || null,
    notes: f.notes || null,
  };
}

export default cuerpoDeDevolucion;
